export interface House {
  id: string;
  clientId: string;
  domain: string;
  username: string;
}

export const DEFAULT_DOMAIN = '.marecolli.casa';

const HOUSES_KEY = 'houses';
const ACTIVE_HOUSE_KEY = 'activeHouseId';
const LEGACY_AUTH_KEY = 'auth';
const LEGACY_BIOMETRIC_KEY = 'biometric';
const LEGACY_GATEWAYS_KEY = 'gateways';
const LEGACY_SENSOR_SOURCES_KEY = 'sensorSources';

export const makeHouseId = (clientId: string, domain: string): string => `${clientId}${domain}`;

export const gatewaysKey = (houseId: string): string => `gateways_${houseId}`;
export const sensorSourcesKey = (houseId: string): string => `sensorSources_${houseId}`;
export const biometricKey = (houseId: string): string => `biometric_${houseId}`;

export const loadHouses = (): House[] => {
  try {
    const raw = localStorage.getItem(HOUSES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const saveHouses = (houses: House[]): void => {
  localStorage.setItem(HOUSES_KEY, JSON.stringify(houses));
};

export const loadActiveHouseId = (): string | null => localStorage.getItem(ACTIVE_HOUSE_KEY);

export const saveActiveHouseId = (id: string | null): void => {
  if (id === null) localStorage.removeItem(ACTIVE_HOUSE_KEY);
  else localStorage.setItem(ACTIVE_HOUSE_KEY, id);
};

const moveKey = (fromKey: string, toKey: string): void => {
  const value = localStorage.getItem(fromKey);
  if (value !== null) {
    localStorage.setItem(toKey, value);
    localStorage.removeItem(fromKey);
  }
};

// Moves a house's namespaced storage (gateways/sensorSources/biometric) from
// one id to another -- used when `restore()` imports a config whose
// clientId/domain differ from the currently active house's, which changes its id.
export const renameHouseData = (oldId: string, newId: string): void => {
  moveKey(gatewaysKey(oldId), gatewaysKey(newId));
  moveKey(sensorSourcesKey(oldId), sensorSourcesKey(newId));
  moveKey(biometricKey(oldId), biometricKey(newId));
};

// One-time migration from the pre-multi-house single-`auth`-key shape. No-op
// if already migrated (houses key exists) or if this is a fresh install
// (no legacy auth key either).
export const migrateLegacyAuth = (): void => {
  if (localStorage.getItem(HOUSES_KEY) !== null) return;
  const rawAuth = localStorage.getItem(LEGACY_AUTH_KEY);
  if (!rawAuth) return;

  let legacy: { clientId?: string; domain?: string; username?: string };
  try {
    legacy = JSON.parse(rawAuth);
  } catch {
    return;
  }
  if (!legacy.clientId || !legacy.domain || !legacy.username) return;

  const id = makeHouseId(legacy.clientId, legacy.domain);
  const house: House = { id, clientId: legacy.clientId, domain: legacy.domain, username: legacy.username };
  saveHouses([house]);
  saveActiveHouseId(id);

  moveKey(LEGACY_BIOMETRIC_KEY, biometricKey(id));
  moveKey(LEGACY_GATEWAYS_KEY, gatewaysKey(id));
  moveKey(LEGACY_SENSOR_SOURCES_KEY, sensorSourcesKey(id));
  localStorage.removeItem(LEGACY_AUTH_KEY);
};
